import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  UploadCloud,
  FileText,
  CheckCircle2,
  XCircle,
  Loader2,
  Rocket,
  AlertTriangle,
  Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHead } from "@/components/brand/Card";
import {
  useReferenceDataSchema,
  useReferenceDataIngestStatus,
  useUploadReferenceDataFile,
  useIngestReferenceData,
  usePromoteReferenceData,
} from "@/hooks/useApi";
import type { ReferenceDataRun } from "@/types/reference-data";
import { formatBytes } from "@/lib/datetime";
import { errMessage } from "@/lib/reference-data-edit";
import { RunStatusPill, Stat } from "@/components/admin/reference-data/shared";
import { promoteMessage } from "@/lib/reference-data-download";
import { ConfirmDialog, TemplateDialog } from "@/components/admin/reference-data/dialogs";

// ---------------------------------------------------------------------------
// Update tab — upload → ingest → sanity report → promote
// ---------------------------------------------------------------------------

export function UpdateTab({
  tableName,
  file,
  setFile,
  runId,
  setRunId,
}: {
  tableName: string;
  file: File | null;
  setFile: (f: File | null) => void;
  runId: string | undefined;
  setRunId: (id: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);

  const { data: schema } = useReferenceDataSchema(tableName);
  const uploadMutation = useUploadReferenceDataFile(tableName);
  const ingestMutation = useIngestReferenceData(tableName);
  const promoteMutation = usePromoteReferenceData(tableName);
  const { data: run } = useReferenceDataIngestStatus(tableName, runId);

  const busy = uploadMutation.isPending || ingestMutation.isPending;
  const polling = run?.status === "running" || run?.status === "queued";
  const report = run?.report ?? null;
  const staged = run?.status === "success" && report?.outcome === "staged";
  const rejected = run?.status === "failed" || report?.outcome === "rejected";

  const pickFile = (f: File | null) => {
    if (!f) return;
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (ext !== "csv" && ext !== "xlsx") {
      toast.error(t("Only .csv and .xlsx files are accepted."));
      return;
    }
    setFile(f);
    setRunId(undefined);
  };

  const handleRun = async () => {
    if (!file) return;
    try {
      const up = await uploadMutation.mutateAsync(file);
      toast.success(t("Uploaded & verified (SHA-256)."));
      const ing = await ingestMutation.mutateAsync({
        object_key: up.object_key,
        filename: up.filename,
        expected_sha256: up.sha256,
      });
      setRunId(ing.run_id);
    } catch (err) {
      toast.error(errMessage(err, t("Upload / ingest failed.")));
    }
  };

  const handlePromote = async () => {
    if (!runId) return;
    try {
      const res = await promoteMutation.mutateAsync(runId);
      toast.success(promoteMessage(t, res));
      setConfirmOpen(false);
      setRunId(undefined);
      setFile(null);
    } catch (err) {
      toast.error(errMessage(err, t("Promotion failed.")));
      setConfirmOpen(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
      {/* Left: upload + sanity */}
      <div className="space-y-6">
        <Card>
          <CardHead overline={t("STEP 1 · UPLOAD")} title={t("Source file")} />
          <div className="p-5">
            <label
              className="block border-2 border-dashed border-[#e8ebf0] rounded-lg py-10 text-center cursor-pointer hover:border-[#ffc83d] hover:bg-[#fffdf5] transition-colors"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                pickFile(e.dataTransfer.files?.[0] ?? null);
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
              />
              <UploadCloud className="w-8 h-8 mx-auto text-[#ffc83d] mb-3" />
              <div className="text-[14px] font-semibold text-[#08254c]">
                {t("Drop a CSV / xlsx file, or click to browse")}
              </div>
            </label>

            {file && (
              <div className="mt-4 flex items-center gap-3 border border-[#e8ebf0] rounded-lg px-4 py-3">
                <FileText className="w-5 h-5 text-[#596b85] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="mono text-[13px] text-[#08254c] truncate">{file.name}</div>
                  <div className="mono muted text-[11px]">{formatBytes(file.size)}</div>
                </div>
                <Button variant="action" size="sm" onClick={handleRun} disabled={busy || polling}>
                  {busy ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("Working…")}
                    </>
                  ) : (
                    <>
                      <UploadCloud className="w-3.5 h-3.5" /> {t("Upload & ingest")}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </Card>

        {runId && (
          <Card>
            <CardHead
              overline={t("STEP 2 · INGEST")}
              title={t("Ingestion run")}
              right={<RunStatusPill status={run?.status} />}
            />
            <div className="p-5">
              {polling && (
                <div className="flex items-center gap-2.5 text-[#596b85] text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t("Parsing, validating and staging rows… this runs asynchronously.")}
                </div>
              )}

              {report && (staged || rejected) && (
                <SanityReport report={report} staged={staged} />
              )}

              {staged && (
                <div className="mt-5 flex items-center justify-between gap-4 bg-[rgba(39,161,85,0.06)] border border-[rgba(39,161,85,0.2)] rounded-lg px-4 py-3.5">
                  <div className="text-[13px] text-[#08254c]">
                    <strong>{t("Sanity passed.")}</strong>{" "}
                    {t("Promote to make this version active. Nothing is live until you do.")}
                  </div>
                  <Button
                    variant="action"
                    size="sm"
                    onClick={() => setConfirmOpen(true)}
                    disabled={promoteMutation.isPending}
                  >
                    <Rocket className="w-3.5 h-3.5" />
                    {t("Promote version")}
                  </Button>
                </div>
              )}

              {rejected && (
                <div className="mt-5 bg-[rgba(212,53,60,0.06)] border border-[rgba(212,53,60,0.2)] rounded-lg px-4 py-3.5 text-[13px] text-[#b02a31]">
                  <strong>{t("Ingestion rejected.")}</strong>{" "}
                  {t("Fix the file and upload again — nothing was staged.")}
                </div>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Right: accepted schema */}
      <Card>
        <CardHead
          overline={t("ACCEPTED COLUMNS")}
          title={t("Upload schema")}
        />
        <div className="p-5">
          <p className="text-[12px] text-[#596b85] mb-3">
            {t(
              "Your file's header row must use these column names.",
            )}
          </p>
          <div className="space-y-1.5">
            {(schema?.columns ?? []).map((c) => (
              <div key={c.name} className="border-b border-[#f2f4f7] pb-1.5 last:border-0">
                <div className="flex items-center justify-between">
                  <span className="mono text-[12px] text-[#08254c]">{c.name}</span>
                  <span className="mono muted text-[10px]">
                    {c.data_type}
                    {!c.is_nullable && <span className="text-[#c25500]"> · {t("required")}</span>}
                  </span>
                </div>
                {/* A closed domain belongs in the upload contract too — the file is
                    rejected for a value outside it, same as an edited cell. */}
                {c.allowed_values && c.allowed_values.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {c.allowed_values.map((v) => (
                      <span
                        key={v}
                        className="mono text-[10px] text-[#596b85] bg-[#f2f4f7] border border-[#e8ebf0] rounded px-1.5 py-px"
                      >
                        {v}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {schema && schema.columns.length === 0 && (
              <span className="text-[12px] text-[#7b8799]">{t("No schema available.")}</span>
            )}
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={() => setTemplateOpen(true)}
            disabled={!schema || schema.columns.length === 0}
            className="w-full mt-4 gap-1.5 py-2.5 font-mono text-[11px] uppercase tracking-[0.06em]"
          >
            <Download className="w-3.5 h-3.5" />
            {t("Download template")}
          </Button>
        </div>
      </Card>

      <TemplateDialog
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        tableName={tableName}
        columns={(schema?.columns ?? []).map((c) => c.name)}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("Promote version")}
        description={t(
          "Promote this ingest to active? The agents will immediately read the new version. In-flight runs stay pinned to the current one.",
        )}
        confirmLabel={t("Promote version")}
        confirmIcon={<Rocket className="w-3.5 h-3.5" />}
        onConfirm={handlePromote}
        loading={promoteMutation.isPending}
      />
    </div>
  );
}

export function SanityReport({
  report,
  staged,
}: {
  report: NonNullable<ReferenceDataRun["report"]>;
  staged: boolean;
}) {
  const { t } = useTranslation();
  const violations = report.violations ?? [];
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {staged ? (
          <CheckCircle2 className="w-4 h-4 text-[#27a155]" />
        ) : (
          <XCircle className="w-4 h-4 text-[#d4353c]" />
        )}
        <span className="font-brand font-bold text-[15px] text-[#08254c]">{t("Sanity report")}</span>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Stat label={t("Rows in file")} value={report.row_count ?? 0} />
        <Stat label={t("New")} value={report.new_count ?? 0} accent="ok" />
        <Stat label={t("Updated")} value={report.updated_count ?? 0} accent="gold" />
      </div>
      {report.accepted_columns && report.accepted_columns.length > 0 && (
        <div className="mt-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#596b85] mb-1.5">
            {t("Accepted columns")}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {report.accepted_columns.map((c) => (
              <span
                key={c}
                className="mono text-[10px] text-[#596b85] bg-[#f2f4f7] border border-[#e8ebf0] rounded px-1.5 py-px"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      )}
      {violations.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[#b02a31] mb-1.5">
            <AlertTriangle className="w-3 h-3" />
            {t("{{n}} violation(s)", { n: violations.length })}
          </div>
          <ul className="space-y-1">
            {violations.map((v, i) => (
              <li key={i} className="text-[12px] text-[#b02a31] font-mono">
                • {v}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
